import React, { FC, JSX, useCallback, useEffect, useMemo, useState } from 'react';

import {
	AzureDevopsTicketCreator,
	AzureTicketDraft,
	BUTTON_TYPES,
	EvButton,
	EvLink
} from '@evinced-private/ui-common';

import { JiraApi } from '../../api/JiraApi';
import { getJiraAccessToken } from '../../api/LocalStorageApi';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { buildAzureTicketCreatorConfig } from '../../services/azureTicketCreatorConfig';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { Report } from '../../types/ReportModel';
import { jiraExportHelper } from '../jira-issue-creator/helpers/jira-export-helper';
import { JiraPopup } from '../jira-popup/JiraPopup';

import { ADD_TO_TICKET_SYSTEM_BTN_ID } from './TicketsSystems.constants';

interface ITicketsSystems {
	issue: Report;
}

export const TicketsSystems: FC<ITicketsSystems> = ({ issue }) => {
	const [isJiraConnected, setJiraConnected] = useState(false);
	const [issueToExport, setIssueToExport] = useState(null);
	const [isJiraModalOpened, setJiraModalOpened] = useState(false);
	const { getToggle, configuration } = useConfiguration();
	const enableAzureIntegration = getToggle(SiteScannerUiToggles.ENABLE_AZURE_INTEGRATION);
	const bypassAzureProcessRules = !getToggle(
		SiteScannerUiToggles.DISABLE_AZURE_BYPASS_PROCESS_RULES
	);

	const exportToTicketsSystem = useCallback(
		(setModalOpened: (flag: boolean) => void): void => {
			setIssueToExport(jiraExportHelper.formatIssueForExport(issue, issue?.note));
			setModalOpened(true);
		},
		[issue]
	);

	const checkIfJiraConnected = useCallback((): void => {
		const jiraToken = getJiraAccessToken();
		setJiraConnected(!!jiraToken);
	}, []);

	useEffect(() => {
		checkIfJiraConnected();
		window.addEventListener('storage', checkIfJiraConnected);

		return () => {
			window.removeEventListener('storage', checkIfJiraConnected);
		};
	}, [checkIfJiraConnected]);

	const renderAddToButton = useCallback(
		(
			ticketSystem: string,
			isConnected: boolean,
			connectUrl: string,
			setModalOpened: (flag: boolean) => void
		) => {
			const title = `Add to ${ticketSystem}`;

			if (isConnected) {
				return (
					<EvButton
						type={BUTTON_TYPES.PRIMARY}
						onClick={() => exportToTicketsSystem(setModalOpened)}
						dataAutomationId={ADD_TO_TICKET_SYSTEM_BTN_ID}
					>
						{title}
					</EvButton>
				);
			}
			return (
				<EvLink
					url={connectUrl}
					linkText={`Connect to ${ticketSystem}`}
					openInNewTab={true}
					className="add-to-jira-link"
				/>
			);
		},
		[exportToTicketsSystem]
	);

	const addToJiraButton = useMemo(() => {
		return renderAddToButton('Jira', isJiraConnected, JiraApi.getJiraAuthURL(), setJiraModalOpened);
	}, [renderAddToButton, isJiraConnected]);

	// Per-tenant Azure OAuth scope from central config overrides the env default (falls back to it,
	// then to DEFAULT_AZURE_SCOPE_NAMES). Memoized so the module's provider resolves once.
	const azureConfig = useMemo(
		() => buildAzureTicketCreatorConfig(configuration?.CONFIG?.AZURE_SCOPES),
		[configuration?.CONFIG?.AZURE_SCOPES]
	);

	// Map the app's Report onto the azure-ticket-creator module's host-agnostic input contract.
	const azureTicket = useMemo<AzureTicketDraft>(() => {
		const formatted = jiraExportHelper.formatIssueForExport(issue, issue?.note);
		return {
			summary: formatted.summary,
			description: formatted.description,
			pageScreenshot: formatted.pageScreenshot,
			elements: formatted.elements.map((element) => ({ boundingBox: element.boundingBox }))
		};
	}, [issue]);

	const renderJiraModal = (): JSX.Element => {
		return (
			<JiraPopup
				isOpen={isJiraModalOpened}
				closeModal={(): void => {
					setJiraModalOpened(false);
				}}
				toggleJiraModal={setJiraModalOpened}
				ticketData={issueToExport}
				onDisconnect={checkIfJiraConnected}
			/>
		);
	};

	return (
		<>
			{enableAzureIntegration ? (
				<AzureDevopsTicketCreator
					config={azureConfig}
					ticket={azureTicket}
					bypassRules={bypassAzureProcessRules}
					dataAutomationId={ADD_TO_TICKET_SYSTEM_BTN_ID}
				/>
			) : (
				addToJiraButton
			)}
			{issueToExport && isJiraModalOpened && renderJiraModal()}
		</>
	);
};
